/**
 * Privilege ID map for UI checks.
 *
 * Replace these values with real `proto_privileges.id` values from MySQL.
 * Keep key names stable so UI conditions stay readable.
 */
export const PRIVILEGES = Object.freeze({
  LOGIN: 1,
  VIEW_PATIENT_CHART: 2,
  UPLOAD_DOCUMENTS: 3,
  EDIT_PATIENT_PROFILE: 4,
  ASSIGN_STAFF_TO_PATIENTS: 5,
  COMMUNICATE_WITH_PATIENTS: 6,
  COMMUNICATE_WITH_NON_PATIENTS: 7,
  ADJUST_PERMISSIONS: 8,
  CREATE_ACCOUNTS: 9,
  VIEW_AUDITS: 10,
  SEND_INTERNAL_MESSAGES: 11,
  VIEW_NOTES: 12,
  WRITE_NOTES: 13,
  MEDICATION_VIEW: 15,
  MEDICATION_ADD_UNORDERED: 16,
  MEDICATION_ADD_ORDERED: 17,
  MEDICATION_IMPORT_FROM_DOCUMENTS: 18,
  MEDICATION_EDIT_UNORDERED: 19,
  MEDICATION_EDIT_ORDERED: 20,
  MEDICATION_HOLD: 21,
  MEDICATION_DISCONTINUE: 22,
  MEDICATION_ADMINISTER: 23,
  MEDICATION_PREPOUR: 24,
  MEDICATION_WITNESS: 25,
  DIAGNOSIS_VIEW: 26,
  DIAGNOSIS_EDIT_AND_DELETE: 27,
  DIAGNOSIS_ADD: 28,
  PROCEDURE_VIEW: 29,
  PROCEDURE_ADD_UNORDERED: 30,
  PROCEDURE_ADD_ORDERED: 31,
  PROCEDURE_IMPORT_FROM_DOCUMENTS: 32,
  PROCEDURE_DISCONTINUE_OR_CANCEL: 33,
  VITALS_VIEW: 34,
  VITALS_ADD_MANUAL: 35,
  ORDER_VIEW: 36,
  ORDER_WRITE: 37,
  ORDER_SIGN: 38,
  METER_DEVICE_VIEW: 39,
  METER_DEVICE_ADD: 40,
  METER_DEVICE_REMOVE: 41,
  METER_CPT_ASSIGNMENTS_VIEW: 42,
  METER_CPT_ASSIGNMENTS_ADD: 43,
  METER_CPT_ASSIGNMENTS_REMOVE: 44,
  EDIT_PATIENT_CONTACT: 45,
  ASSIGN_NON_PATIENTS_TO_GROUPS: 46,
  VIEW_USE_BILLING_PAGE: 47,
  APPLICATION_TIMER: 49,
  ALLERGY_ADD: 50,
  ALLERGY_EDIT_AND_DELETE: 51,
  // Directives feature (ids match db/migrations/mysql/20260621000000_directives_schema.sql).
  // Former set_pdf_as_directive (48) removed — see 20260810210000_ref_drop_set_pdf_as_directive_privilege.sql.
  DIRECTIVE_READ: 52,
  DIRECTIVE_UPLOAD_MODIFY: 53,
  DIRECTIVE_APPROVE_REAUTHORIZE: 54,
  HELP_VIEW: 55,
  HELP_MANAGE_PLATFORM: 56,
  HELP_MANAGE_ORG: 57,
  // Scheduling module (db/migrations/mysql/20260717000001_patient_scheduling_privilege.sql).
  // Per-patient: only counts when granted via a group owned by the specific
  // patient being viewed — see src/composables/useSchedulingAccess.ts.
  // NOT 55 — the Azure dev DB already had ids 55-57 claimed by an
  // out-of-band "in-app help" feature untracked by any migration in this
  // repo; renumbered to 58/59 to avoid the collision (see that migration's
  // comment for the full story).
  PATIENT_SCHEDULING_ADD: 58,
  // Elevates a provider/nurse/HHA/allied pro to admin-equivalent access
  // throughout Scheduling (every caregiver's + patient's calendar, schedule
  // for anyone) — see src/composables/useSchedulingAccess.ts.
  SCHEDULE_ADMINISTER: 59,
  // Gates the Administrator-only aggregated view across every caregiver's
  // license/insurance/misc documents org-wide (PdfsProviderDocuments.vue,
  // ListCredentialDocuments scope 'org') - a Provider/Nurse/HHA/Allied viewing their own
  // documents is never gated by this. See
  // db/migrations/mysql/20260805120000_caregiver_documents_privilege.sql.
  VIEW_CAREGIVER_DOCUMENTS: 60,
  // Managing the per-state re-authorization rules for the whole organization
  // (Administration -> Directive Re-authorization Rules). Deliberately NOT
  // DIRECTIVE_APPROVE_REAUTHORIZE (54): that is permission to sign off one
  // patient's record and requires an NPI; this is an administrative act that
  // does not. See db/migrations/mysql/20260807212407_ref_directive_policy_privilege.sql.
  DIRECTIVE_POLICY_MANAGE: 61,
  // Gates the "Account Access" panel on the Edit User form - changing
  // another user's username/email/password (Postgres LA_user + MySQL
  // account_user/nurses/providers). Administrators only by default; not
  // backfilled broadly like view_caregiver_documents since this is a new
  // capability, not a re-gate of existing behavior. See
  // db/migrations/mysql/20260812120000_manage_account_credentials_privilege.sql.
  MANAGE_ACCOUNT_CREDENTIALS: 62,
  // Gates the Users and Status page — Administration menu and /myOrg route.
  // See db/migrations/mysql/20260828163000_my_org_view_privilege.sql.
  MY_ORG_VIEW: 63,
  // Dedicated gate for generating/downloading the Privileges Audit Report
  // (XLSX/JSON) — adjust_permissions/view_audits no longer qualify.
  // Administrators only by default. NOT 63 — my_org_view claimed that id on
  // main first. See
  // db/migrations/mysql/20260831235000_ref_generate_privileges_privilege.sql.
  GENERATE_PRIVILEGES: 64,
  // Scope, orthogonal to verb. 52/53/54 each mean "for my assigned patients";
  // 65 widens whichever of them you hold to every patient in the org. Granted
  // by default to ADMINISTRATORS only. Break-glass grants directive_read (52)
  // for emergency viewing but deliberately does NOT grant 65 (assigned-patient
  // scope only). See src/utils/directivesScope.js and
  // db/migrations/mysql/20260908160645_ref_directive_all_patients_privilege.sql.
  DIRECTIVE_ALL_PATIENTS: 65,
  // A patient submitting their OWN directive. Self-scoped by definition — the
  // server pairs it with an ownership check on patient_pid. Deliberately not
  // DIRECTIVE_UPLOAD_MODIFY (53): that also unlocks add-codes/confirm/revoke,
  // and those gates do not verify the record belongs to the caller, so granting
  // 53 to patients would let one patient revoke another's pending directive.
  // See db/migrations/mysql/20260908171359_ref_directive_create_own_privilege.sql.
  DIRECTIVE_CREATE_OWN: 66,
});

/**
 * Optional helper for guard clauses:
 *   if (!canPrivilege(privilegesStore, PRIVILEGES.EDIT_MEDICATIONS)) return;
 */
export function canPrivilege(privilegesStore, privilegeId) {
  if (!privilegesStore || typeof privilegesStore.effectiveGranted !== "function") {
    return false;
  }
  return privilegesStore.effectiveGranted(privilegeId);
}

